import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// import { ReactComponent as DownArrow } from '../../../../assets/icons/down-arrow.svg';
import { ReactComponent as ArabicFlag } from '../../../../assets/icons/flag-icons/arabic.svg';
import { ReactComponent as EnglishFlag } from '../../../../assets/icons/flag-icons/english.svg';
import { ReactComponent as FrenchFlag } from '../../../../assets/icons/flag-icons/french.svg';
import { ReactComponent as SpanishFlag } from '../../../../assets/icons/flag-icons/spanish.svg';
import { ReactComponent as PlusIcon } from '../../../../assets/icons/plus.svg';
// import { ReactComponent as UpArrow } from '../../../../assets/icons/up-arrow.svg';
import DefaultStationImage from '../../../../assets/images/default-station.png';
import { appCurrentBranding } from '../../../../config/branding';
import { PortalType, branding } from '../../../../config/enum';
import {
  getTimeZones,
  getUserTimeZone,
  normalizeTimeZone,
} from '../../../../config/timezonesLabel';
import { useGoogleMaps } from '../../../../contexts/google-map-context/GoogleMapContent';
import { usePortal } from '../../../../contexts/PortalContext/PortalContext';
import {
  NewProgressProps as NewProgressProperties,
  SingleFileUploadProgress,
} from '../../../../interface/file-upload-stream.interface';
import {
  Amenity,
  LocationDetails,
} from '../../../../interface/partner-interfaces/location-management.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  setUnUploadedFileData,
  updateFileUploadProgress,
} from '../../../../redux/slices/FileUploadStreamSlice';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import {
  fetchPendingNotification,
  fetchPendingStations,
} from '../../../../redux/slices/Partners/PendingLocationManagementSlice';
import MyButton from '../../../../shared-components/button/Button';
import CustomAddressInput from '../../../../shared-components/custom-address-input/CustomAddressInput';
// import CustomCheckbox from '../../../../shared-components/custom-check-box/CustomCheckBox';
import CustomFileInput from '../../../../shared-components/custom-file-input/CustomFileInput';
import CustomInput from '../../../../shared-components/custom-input/CustomInput';
import CustomPhoneInput from '../../../../shared-components/custom-input/CustomPhoneInput';
import CustomInputSwitch from '../../../../shared-components/custom-input-switch/CustomInputSwitch';
import CustomMediaUpload from '../../../../shared-components/custom-media-upload/CustomMediaUpload';
import CustomTimePicker from '../../../../shared-components/custom-time-input/CustomTimePicker';
import FileUploadProgress from '../../../../shared-components/file-upload-progress/FileUploadProgress';
import FormCancelWarning from '../../../../shared-components/form-cancel-warning/FormCancelWarning';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import SelectFiled from '../../../../shared-components/select-field/SelectField';
import SelectLocation from '../../../../shared-components/select-location/SelectLocation';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
import TabToggle from '../../../../shared-components/tab-toggle/TabToggle';
import FacilityDetailsSidePanel from '../../../../shared-components/table-side-panel/FacilityDetailsSidePanel';
import SidePaneModel from '../../../../shared-components/table-side-panel/SidePaneModel';
import { SanitizeFileName } from '../../../../shared-functions/StringFunctions';
import {
  addressValidation,
  checkFileSizeValidation,
  isAddressResolved,
  stringValidation,
} from '../../../../shared-functions/Validation';
import deviceApi from '../../../../utils/api/partner/device-partner';
import station from '../../../../utils/api/partner/station';
import AddFacilityAccount from '../../../account-management/components/add-facility-account';
// import PageTitle from '../../../shared-components/page-title/PageTitle';

import AmenitiesInput from './AmenitiesInput';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
}

/**
 * Add new station component
 */
interface AddLocationProperties {
  onClose?: () => void;
  onSuccess?: () => void;
  fetchByPartner?: string;
}

interface DayOperatingHours {
  isOpen: boolean;
  from: string;
  to: string;
  is24Hours?: boolean;
}
interface OperatingHours {
  allDay: boolean;
  monday: DayOperatingHours;
  tuesday: DayOperatingHours;
  wednesday: DayOperatingHours;
  thursday: DayOperatingHours;
  friday: DayOperatingHours;
  saturday: DayOperatingHours;
  sunday: DayOperatingHours;
}

interface InputInterface {
  label: string;
  value: string;
  description: string;
}

interface AddDeviceProperties {
  _id: string;
  accountCustomId: string;
  accountName: string;
}

type Option = {
  value: string | number;
  label: string;
};

type DayKey = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

/**
 * Get language options with fresh JSX element flags
 * @returns {any[]} Array of language options
 */
const getLanguageOptions = () => [
  {
    value: 'English',
    label: 'English',
    labelNode: (
      <div className="language-option-node">
        <EnglishFlag style={{ width: 24, height: 16, borderRadius: 2 }} />
        <span>English</span>
      </div>
    ),
  },
  {
    value: 'Arabic',
    label: 'Arabic',
    labelNode: (
      <div className="language-option-node">
        <ArabicFlag style={{ width: 24, height: 16, borderRadius: 2 }} />
        <span>Arabic</span>
      </div>
    ),
  },
  {
    value: 'French',
    label: 'French',
    labelNode: (
      <div className="language-option-node">
        <FrenchFlag style={{ width: 24, height: 16, borderRadius: 2 }} />
        <span>French</span>
      </div>
    ),
  },
  {
    value: 'Spanish',
    label: 'Spanish',
    labelNode: (
      <div className="language-option-node">
        <SpanishFlag style={{ width: 24, height: 16, borderRadius: 2 }} />
        <span>Spanish</span>
      </div>
    ),
  },
];

/**
 * Format 24hr time to 12hr time
 * @param {any} timeString time string
 */
// const formatTime12Hour = (timeString: string) => {
//   if (!timeString) return '';
//   const [h, m] = timeString.split(':');
//   let hours = parseInt(h, 10);
//   const ampm = hours >= 12 ? 'PM' : 'AM';
//   hours %= 12;
//   hours ||= 12; // the hour '0' should be '12'
//   const formattedHours = hours < 10 ? `0${hours}` : hours;
//   return `${formattedHours}:${m} ${ampm}`;
// };

/**
 * Add new station component
 * @param {Readonly<AddLocationProperties>} properties - The component properties
 * @returns {React.ReactElement} component
 */
function AddLocation({ onClose, onSuccess, fetchByPartner }: Readonly<AddLocationProperties>) {
  const { t, i18n } = useTranslation('location');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [accountNameList, setAccountNameList] = useState<InputInterface[]>([]);
  const [isAccountLoading, setIsAccountLoading] = useState<boolean>(true);
  const [showFacilityPanel, setShowFacilityPanel] = useState(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [addAccountSidePanelOpen, setAddAccountSidePanelOpen] = useState<boolean>(false);
  const [stationDetails, setLocationDetails] = useState<LocationDetails>({
    name: '',
    accountCustomId: fetchByPartner ?? '',
    landOwnerShipDocument: [],
    ebReport: [],
    electricityContractType: '',
    timeZone: getUserTimeZone(),
    operatingHours: {
      allDay: true,
      monday: { isOpen: true, from: '00:00', to: '23:59', is24Hours: true },
      tuesday: { isOpen: true, from: '00:00', to: '23:59', is24Hours: true },
      wednesday: { isOpen: true, from: '00:00', to: '23:59', is24Hours: true },
      thursday: { isOpen: true, from: '00:00', to: '23:59', is24Hours: true },
      friday: { isOpen: true, from: '00:00', to: '23:59', is24Hours: true },
      saturday: { isOpen: true, from: '00:00', to: '23:59', is24Hours: true },
      sunday: { isOpen: true, from: '00:00', to: '23:59', is24Hours: true },
    },
    address: '',
    latitude: null,
    longitude: null,
    medias: [],
    postalCode: null,
    countryNameCode: null,
    amenities: [],
    electricityCostByUtilityProvider: null,
    isGuestChargingExclusive: false,
    emergencyContact: '',
    countryCode: appCurrentBranding === branding.VOLTIE ? '1' : '966',
    geoLocations: [],
  });
  const { portalType } = usePortal();
  const { limit: unapprovedLimit, page: unapprovedPage } = useAppSelector(
    (state) => state.partnersPendingLocation,
  );
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
  });
  const [formStatus, setFormStatus] = useState<any>({
    buttonLoader: false,
    showFormErrors: false,
  });
  const [latLongError, setLatLongError] = useState<string>('');

  const { isLoaded } = useGoogleMaps();

  const [dynamicAmenities, setDynamicAmenities] = useState<Amenity[]>([]);
  const [isFetchingAmenities, setIsFetchingAmenities] = useState(false);
  // eslint-disable-next-line unicorn/prevent-abbreviations
  const [navigationDirections, setNavigationDirections] = useState<
    { language: string; direction: string }[]
  >([{ language: '', direction: '' }]);
  // eslint-disable-next-line unicorn/prevent-abbreviations
  const [additionalDocs, setAdditionalDocs] = useState<File[][]>([]);

  /**
   * Filter and return language options not already selected
   * @param {any} currentIndex - The current index of the dropdown
   */
  const getAvailableLanguageOptions = (currentIndex: number) => {
    const selectedLangs = navigationDirections
      .map((item, index) => (index !== currentIndex ? item.language : ''))
      .filter(Boolean);
    return getLanguageOptions().filter((option) => !selectedLangs.includes(option.value));
  };

  /**
   * Handle direction text change and enforce language characters
   * @param {any} index - The index of the row being changed
   * @param {any} value - The input text value
   */
  const handleDirectionsChange = (index: number, value: string) => {
    const lang = navigationDirections[index].language;
    let filteredValue = value;
    if (lang === 'English') {
      filteredValue = value.replace(/[^a-zA-Z0-9\s.,'()"\-_?!]/g, '');
    } else if (lang === 'Arabic') {
      filteredValue = value.replace(
        /[^\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF0-9\s.,'()"\-_?!]/g,
        '',
      );
    } else if (lang === 'French') {
      filteredValue = value.replace(/[^a-zA-ZÀ-ÿ0-9\s.,'()"\-_?!]/g, '');
    } else if (lang === 'Spanish') {
      filteredValue = value.replace(/[^a-zA-ZáéíóúüñÁÉÍÓÚÜÑ0-9\s.,'()"\-_?!]/g, '');
    }
    const updated = [...navigationDirections];
    updated[index].direction = filteredValue;
    setNavigationDirections(updated);
  };

  /**
   * Add a new navigation direction row
   */
  const handleAddNavigationDirection = () => {
    if (navigationDirections.length < getLanguageOptions().length) {
      setNavigationDirections([...navigationDirections, { language: '', direction: '' }]);
    }
  };

  const listOfCountryCode = [
    {
      label: t('standardResidential'),
      value: 'standard-residential',
    },
    {
      label: t('commercial'),
      value: 'commercial',
    },
    {
      label: t('industrial'),
      value: 'industrial',
    },
    {
      label: t('evChargingSpecificContracts'),
      value: 'ev-charging-specific-contracts',
    },
  ];

  /**
   * Handle input value change
   * @param {any} name - The name
   * @param {any} changeValue - The changeValue
   */
  const handleStationDetailChange = (
    name: string,
    changeValue: string | number | File[] | Amenity[] | boolean,
  ) => {
    setLocationDetails({
      ...stationDetails,
      [name]: changeValue,
    });
  };

  /**
   * Handle Change address
   * @param {string} name - The name
   * @param {string | number} address - The address
   * @param {number | null} latitude - The latitude
   * @param {number | null} longitude - The longitude
   * @param {any[]} geoLocation - The geoLocation
   * @param {string | null} [postalCode] - The postalCode
   * @param {string | null} [countryCode] - The countryCode
   * @returns {void}
   */
  const handleChangeAddress = (
    name: string,
    address: string | number,
    latitude: number | null,
    longitude: number | null,
    geoLocation: any[],
    postalCode?: string | null,
    countryCode?: string | null,
  ) => {
    console.log(address, latitude, longitude, geoLocation, postalCode, countryCode);
    setLocationDetails({
      ...stationDetails,
      [name]: address,
      latitude,
      longitude,
      geoLocations: geoLocation,
      postalCode: postalCode || null,
      countryNameCode: countryCode || null,
    });
    setLatLongError('');
  };

  /**
   * Handle lat & long change
   * @param {number} latitude - The latitude
   * @param {number} longitude - The longitude
   * @param {string} [address] - The address
   * @param {any[]} [geoLocation] - The geoLocation
   * @param {string | null} [postalCode] - The postalCode
   * @param {string | null} [countryCode] - The countryCode
   * @returns {void}
   */
  const handleChangeLatLong = (
    latitude: number,
    longitude: number,
    address?: string,
    geoLocation?: any[],
    postalCode?: string | null,
    countryCode?: string | null,
  ) => {
    console.log(address, latitude, longitude, geoLocation, postalCode, countryCode);
    setLocationDetails((previousValue) => ({
      ...previousValue,
      latitude,
      longitude,
      // Map submit without a click sends an empty geoLocation - keep the already resolved values
      geoLocations: geoLocation?.length ? geoLocation : previousValue.geoLocations,
      postalCode: geoLocation?.length ? postalCode || null : previousValue.postalCode,
      countryNameCode: geoLocation?.length ? countryCode || null : previousValue.countryNameCode,
      ...(address ? { address } : {}),
    }));
    setLatLongError('');
  };

  /**
   * Handle operating hours change
   * @param {any} day - The day
   * @param {any} field - The field
   * @param {any} value - The value
   */
  const handleOperatingHoursChange = (
    day: DayKey,
    field: 'isOpen' | 'from' | 'to' | 'is24Hours',
    value: any,
  ) => {
    setLocationDetails((previous) => {
      const updatedHours = {
        ...previous.operatingHours!,
        [day]: {
          ...previous.operatingHours![day],
          [field]: value,
        },
      };

      const daysList = [
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
        'sunday',
      ] as const;

      const isAllDayOpen = daysList.every((d) => {
        const dData = updatedHours[d];
        return (
          dData?.isOpen && (dData?.is24Hours || (dData?.from === '00:00' && dData?.to === '23:59'))
        );
      });

      updatedHours.allDay = isAllDayOpen;

      return {
        ...previous,
        operatingHours: updatedHours,
      };
    });
  };

  /**
   * Handle All Day toggle
   * @param {any} name - The name
   * @param {any} value - The value
   */
  const handleAllDayToggle = (name: string, value: boolean) => {
    setLocationDetails((previous) => {
      const updatedOperatingHours = { ...previous.operatingHours! };
      updatedOperatingHours.allDay = value;

      if (value) {
        const fullDay = { isOpen: true, from: '00:00', to: '23:59', is24Hours: true };
        updatedOperatingHours.monday = fullDay;
        updatedOperatingHours.tuesday = fullDay;
        updatedOperatingHours.wednesday = fullDay;
        updatedOperatingHours.thursday = fullDay;
        updatedOperatingHours.friday = fullDay;
        updatedOperatingHours.saturday = fullDay;
        updatedOperatingHours.sunday = fullDay;
      } else {
        const customDay = { isOpen: true, from: '09:00', to: '18:00', is24Hours: false };
        updatedOperatingHours.monday = customDay;
        updatedOperatingHours.tuesday = customDay;
        updatedOperatingHours.wednesday = customDay;
        updatedOperatingHours.thursday = customDay;
        updatedOperatingHours.friday = customDay;
        updatedOperatingHours.saturday = customDay;
        updatedOperatingHours.sunday = customDay;
      }

      return {
        ...previous,
        operatingHours: updatedOperatingHours,
      };
    });
  };

  /**
   * Handle Day Toggle
   * @param {any} day - The day
   * @param {any} isOpen - The isOpen
   */
  const handleDayToggle = (day: DayKey, isOpen: boolean) => {
    handleOperatingHoursChange(day, 'isOpen', isOpen);
  };

  /**
   * Function to get and handle file upload progress
   * @param {any} progress - The progress
   */
  const handleUploadProgress = (progress: NewProgressProperties) => {
    if (progress?.fileName && progress?.percentage) {
      dispatch(updateFileUploadProgress(progress));
    }
  };

  /**
   * Navigate back
   */
  const handleNavigateBack = () => {
    setLocationDetails({
      name: '',
      accountCustomId: fetchByPartner ?? '',
      landOwnerShipDocument: [],
      ebReport: [],
      electricityContractType: '',
      timeZone: getUserTimeZone(),
      operatingHours: {
        allDay: false,
        monday: { isOpen: true, from: '00:00', to: '23:59' },
        tuesday: { isOpen: true, from: '00:00', to: '23:59' },
        wednesday: { isOpen: true, from: '00:00', to: '23:59' },
        thursday: { isOpen: true, from: '00:00', to: '23:59' },
        friday: { isOpen: true, from: '00:00', to: '23:59' },
        saturday: { isOpen: true, from: '00:00', to: '23:59' },
        sunday: { isOpen: false, from: '00:00', to: '23:59' },
      },
      address: '',
      latitude: null,
      longitude: null,
      medias: [],
      amenities: [],
      electricityCostByUtilityProvider: null,
      isGuestChargingExclusive: false,
      emergencyContact: '',
    });
    setNavigationDirections([{ language: '', direction: '' }]);
    setAdditionalDocs([]);
    setModalBoxStatus('close');

    if (onClose) {
      onClose();
    } else {
      navigate('../approved');
    }
  };

  /**
   * Show warning when navigate back
   */
  const handleClickCancel = () => {
    setModalBoxDetail({
      title: '',
      content: (
        <FormCancelWarning
          title={t('cancelTitle')}
          description={t('cancelDescription')}
          cancelButtonLabel={t('cancelButton')}
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitButtonLabel={t('okButton')}
          submitButtonOnClick={handleNavigateBack}
        />
      ),
      notificationModal: true,
    });
    setModalBoxStatus('open');
  };

  /**
   * Add station API call
   */
  const handleSubmitLocationDetails = () => {
    if (
      (stationDetails.ebReport.length > 0 &&
        !checkFileSizeValidation(stationDetails.ebReport, 1, 1)) ||
      (stationDetails.landOwnerShipDocument.length > 0 &&
        !checkFileSizeValidation(stationDetails.landOwnerShipDocument, 1, 1))
    ) {
      setFormStatus({
        showFormErrors: true,
        buttonLoader: false,
      });
      dispatch(
        setNotificationMessage({
          status: true,
          message: t('documentRequiredError'),
          type: 'error',
          code: 0,
        }),
      );
      return;
    }
    const hasInvalidElectricityCost =
      stationDetails.electricityCostByUtilityProvider !== null &&
      stationDetails.electricityCostByUtilityProvider !== undefined &&
      Number(stationDetails.electricityCostByUtilityProvider) < 0.1;

    if (
      stringValidation(1, 100, stationDetails.name) &&
      ((PortalType.NETWORKS !== portalType && PortalType.APP !== portalType) ||
        stringValidation(1, 100, stationDetails.accountCustomId)) &&
      addressValidation(500, stationDetails.address) &&
      isAddressResolved(stationDetails) &&
      stationDetails.latitude &&
      stationDetails.longitude &&
      !hasInvalidElectricityCost
    ) {
      setFormStatus({
        showFormErrors: true,
        buttonLoader: true,
      });
      const formData = new FormData();

      // Append simple fields
      formData.append('name', stationDetails.name);
      const isNetworkOrApp =
        PortalType.NETWORKS === portalType || PortalType.APP === portalType;
      const accountIdToAppend = isNetworkOrApp
        ? stationDetails?.accountCustomId
        : stationDetails?.accountCustomId ||
          fetchByPartner ||
          (portalType === PortalType.PARTNER ? accountNameList[0]?.value : '') ||
          '';

      if (accountIdToAppend) {
        formData.append('accountCustomId', accountIdToAppend);
      }
      if (stationDetails.electricityContractType) {
        formData.append('electricityContractType', stationDetails.electricityContractType);
      }
      if (stationDetails.countryCode && stationDetails.emergencyContact) {
        formData.append('countryCode', stationDetails.countryCode);
        formData.append('emergencyContact', stationDetails.emergencyContact);
      }
      formData.append('address', stationDetails.address);
      if (
        stationDetails.electricityCostByUtilityProvider !== null &&
        stationDetails.electricityCostByUtilityProvider !== undefined
      ) {
        formData.append(
          'electricityCostByUtilityProvider',
          String(stationDetails.electricityCostByUtilityProvider),
        );
      }
      formData.append('isGuestChargingExclusive', 'false');
      formData.append(
        'latitude',
        stationDetails.latitude ? stationDetails.latitude.toString() : '',
      );

      formData.append(
        'longitude',
        stationDetails.longitude ? stationDetails.longitude.toString() : '',
      );
      formData.append('time_zone', stationDetails.timeZone || '');

      /** operating hours */
      const days = [
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
        'sunday',
      ] as const;

      days.forEach((day) => {
        const dayData = stationDetails.operatingHours?.[day];

        if (dayData?.isOpen) {
          formData.append(`operatingHours[${day}][open]`, dayData.from);
          formData.append(`operatingHours[${day}][close]`, dayData.to);
          formData.append(`operatingHours[${day}][status]`, 'open');
        } else {
          formData.append(`operatingHours[${day}][status]`, 'closed');
        }
      });

      // Append files for landOwnerShipDocument if it's an array
      if (Array.isArray(stationDetails.landOwnerShipDocument)) {
        stationDetails.landOwnerShipDocument.forEach((file) => {
          formData.append(`landOwnerShipDocument`, file);
        });
      }

      // Append files for ebReport if it's an array
      if (Array.isArray(stationDetails.ebReport)) {
        stationDetails.ebReport.forEach((file) => {
          formData.append(`ebReport`, file);
        });
      }

      // Append additional documents
      additionalDocs.forEach((filesList) => {
        if (Array.isArray(filesList)) {
          filesList.forEach((file) => {
            formData.append(`additionalDocuments`, file);
          });
        }
      });

      // Append multiple files for medias (if it's an array)
      if (stationDetails.medias && Array.isArray(stationDetails.medias)) {
        stationDetails.medias.forEach((media) => {
          formData.append(`medias`, media);
        });
      }

      // Append multiple amenities
      if (stationDetails.amenities && Array.isArray(stationDetails.amenities)) {
        stationDetails.amenities.forEach((amenity, index) => {
          formData.append(`amenities[${index}]`, amenity);
        });
      }

      const languageCountryMap: Record<string, string> = {
        Arabic: 'Saudi Arabia',
        English: 'United States',
        French: 'France',
        Spanish: 'Spain',
      };

      const activeNavigationDirections = navigationDirections.filter((item) => item.language);

      activeNavigationDirections.forEach((nav, index) => {
        const country = languageCountryMap[nav.language] || '';
        formData.append(`navigationDirections[${index}][country]`, country);
        if (nav.language) formData.append(`navigationDirections[${index}][language]`, nav.language);
        if (nav.direction)
          formData.append(`navigationDirections[${index}][direction]`, nav.direction);
      });
      const binaryFileData: SingleFileUploadProgress[] = [
        ...stationDetails.landOwnerShipDocument,
        ...stationDetails.medias,
        ...stationDetails.ebReport,
        ...additionalDocs.flat(),
      ]?.map((fileData) => ({
        fileName: SanitizeFileName(fileData.name),
        progress: 0,
        status: 'pending',
      }));

      if (binaryFileData?.length) {
        dispatch(setUnUploadedFileData(binaryFileData));
        setModalBoxStatus('open');
        setModalBoxDetail({
          title:
            binaryFileData?.length === 1
              ? t('uploadingFile')
              : `${binaryFileData?.length} ${t('uploadingFiles')}`,
          content: <FileUploadProgress />,
          notificationModal: false,
        });
      }

      if (stationDetails.geoLocations) {
        stationDetails.geoLocations.forEach((geo, index) => {
          formData.append(`geoLocations[${index}][name]`, geo.name);
          formData.append(`geoLocations[${index}][key]`, geo.key);
          formData.append(`geoLocations[${index}][value]`, geo.value);
        });
        if (stationDetails.postalCode) formData.append(`postalCode`, stationDetails.postalCode);
        if (stationDetails.countryNameCode)
          formData.append(`countryNameCode`, stationDetails.countryNameCode);
      }

      station
        .addStation(formData, handleUploadProgress)
        .then(() => {
          dispatch(setUnUploadedFileData([]));
          onSuccess?.(); // Trigger refresh
          dispatch(fetchPendingNotification());
          dispatch(fetchPendingStations({ page: unapprovedPage, limit: unapprovedLimit }));
          // handleNavigateBack(); // Navigate back (close modal/redirect)

          setModalBoxDetail({
            title: null,
            content: (
              <SuccessNotification
                title={t('stationAddedSuccessfully')}
                description={t('stationSavedSuccessfully')}
                notificationCloseTimer={3000}
                onNotificationClose={handleNavigateBack}
              />
            ),
            notificationModal: true,
          });
          setModalBoxStatus('open');
        })
        .catch((error) => {
          console.error(error);
          setFormStatus({
            showFormErrors: false,
            buttonLoader: false,
          });
          setModalBoxStatus('close');

          const isNetworkError =
            error?.message === 'Failed to fetch' ||
            error?.message?.includes('fetch') ||
            error?.message === 'Network Error';
          const totalNewFiles =
            (stationDetails.landOwnerShipDocument?.length || 0) +
            (stationDetails.medias?.length || 0) +
            (stationDetails.ebReport?.length || 0) +
            (additionalDocs.flat()?.length || 0);

          let errorMessage = '';
          if (error?.response?.status === 413 || (isNetworkError && totalNewFiles > 0)) {
            errorMessage = t('requestEntityTooLarge');
          } else {
            errorMessage = error?.response?.data?.message || t('somethingWentWrong');
          }

          const isDuplicateLatLong =
            errorMessage.includes(
              'A charging station already exists with the same latitude and longitude',
            ) ||
            errorMessage
              .toLowerCase()
              .includes('already exists with the same latitude and longitude') ||
            errorMessage.toLowerCase().includes('duplicate latitude and longitude') ||
            errorMessage.toLowerCase().includes('latitude and longitude already exists');

          setLatLongError(isDuplicateLatLong ? errorMessage : '');

          dispatch(
            setNotificationMessage({
              status: true,
              message: errorMessage,
              type: 'error',
              code: error?.response?.status || 0,
            }),
          );
        });
    } else {
      setFormStatus({
        showFormErrors: true,
        buttonLoader: false,
      });
    }
  };
  const [openTab, setOpenTab] = useState<number>(0);

  /**
   * Change tab + update URL
   * @param {any} tabLabel - The tabLabel
   */
  const changeTabPages = (tabLabel: string) => {
    const index = [t('stationInformation'), t('operatingHours')].indexOf(tabLabel);
    if (index !== -1) {
      setOpenTab(index);
    }
  };
  const timeZoneOptions = React.useMemo(() => getTimeZones(), []);

  /**
   * Fetch account name list API and set the account name list state with the response data.
   */
  const handleAccountNameList = async () => {
    try {
      setIsAccountLoading(true);
      const response = await deviceApi.fetchAccountNameList();
      setAccountNameList(
        response.data.map((account: AddDeviceProperties) => ({
          label: account?.accountName,
          value: account?.accountCustomId,
        })),
      );
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    } finally {
      setIsAccountLoading(false);
    }
  };
  /**
   * Handle open add partner side panel
   */
  const handleOpenAddPartner = () => {
    setAddAccountSidePanelOpen(true);
  };

  /**
   * Handling information icon click
   * @param {any} option - receiving options as a generic
   * @returns {any} - returning options values
   */
  const handleOpenFacilityInformation = (option: Option) => {
    navigate(`?accountCustomId=${option.value}`);
    setShowFacilityPanel(true);
  };

  useEffect(() => {
    handleAccountNameList();
  }, []);

  useEffect(() => {
    if (stationDetails.latitude && stationDetails.longitude && isLoaded && window.google) {
      setIsFetchingAmenities(true);

      const mapLocation = new window.google.maps.LatLng(
        parseFloat(String(stationDetails.latitude)),
        parseFloat(String(stationDetails.longitude)),
      );

      const service = new window.google.maps.places.PlacesService(document.createElement('div'));

      service.nearbySearch(
        {
          location: mapLocation,
          radius: 1000,
        },
        (results, status) => {
          if (status === globalThis.google.maps.places.PlacesServiceStatus.OK && results) {
            const fetchedAmenities: Amenity[] = [];
            results.forEach((place) => {
              const types = place.types || [];
              if (types.includes('gas_station') && !fetchedAmenities.includes('Gas Station'))
                fetchedAmenities.push('Gas Station');
              if (
                (types.includes('cafe') || types.includes('library')) &&
                !fetchedAmenities.includes('Free Wifi')
              )
                fetchedAmenities.push('Free Wifi');
              if (
                (types.includes('gas_station') ||
                  types.includes('shopping_mall') ||
                  types.includes('restaurant')) &&
                !fetchedAmenities.includes('Restroom')
              )
                fetchedAmenities.push('Restroom');
              if (
                (types.includes('lodging') ||
                  types.includes('transit_station') ||
                  types.includes('airport')) &&
                !fetchedAmenities.includes('Lounge')
              )
                fetchedAmenities.push('Lounge');
              if (
                (types.includes('cafe') || types.includes('bakery')) &&
                !fetchedAmenities.includes('Cafe')
              )
                fetchedAmenities.push('Cafe');
              if (
                (types.includes('shopping_mall') ||
                  types.includes('store') ||
                  types.includes('supermarket') ||
                  types.includes('convenience_store')) &&
                !fetchedAmenities.includes('Shopping')
              )
                fetchedAmenities.push('Shopping');
              if (
                (types.includes('restaurant') || types.includes('meal_takeaway')) &&
                !fetchedAmenities.includes('Restaurant')
              )
                fetchedAmenities.push('Restaurant');
              if (
                (types.includes('park') || types.includes('spa') || types.includes('campground')) &&
                !fetchedAmenities.includes('Relaxing Area')
              )
                fetchedAmenities.push('Relaxing Area');
              if (
                (types.includes('storage') || types.includes('transit_station')) &&
                !fetchedAmenities.includes('Locker')
              )
                fetchedAmenities.push('Locker');
              if (types.includes('car_repair') && !fetchedAmenities.includes('Work Shop'))
                fetchedAmenities.push('Work Shop');
              if (types.includes('car_wash') && !fetchedAmenities.includes('Car Wash'))
                fetchedAmenities.push('Car Wash');
            });
            setDynamicAmenities(fetchedAmenities);
            setLocationDetails((previous) => ({
              ...previous,
              amenities: fetchedAmenities,
            }));
          } else {
            setDynamicAmenities([]);
            setLocationDetails((previous) => ({
              ...previous,
              amenities: [],
            }));
          }
          setIsFetchingAmenities(false);
        },
      );
    }
  }, [stationDetails.latitude, stationDetails.longitude, isLoaded]);

  if (showFacilityPanel) {
    return (
      <FacilityDetailsSidePanel
        actionActive
        isEditMode={isEditMode}
        onEditModeChange={setIsEditMode}
        fetchIdFrom="queryParams"
        onBack={() => {
          setShowFacilityPanel(false);
          setIsEditMode(false);
        }}
      />
    );
  }

  return (
    <div className="add-station-panel" dir={i18n.dir()}>
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />
      <SidePaneModel
        open={addAccountSidePanelOpen}
        title=""
        closePanel={() => setAddAccountSidePanelOpen(false)}
        content={
          <AddFacilityAccount
            closeModal={(message) => {
              setAddAccountSidePanelOpen(false);
              if (message) {
                handleAccountNameList();
                setModalBoxDetail({
                  title: '',
                  content: (
                    <SuccessNotification
                      title={message}
                      description=""
                      notificationCloseTimer={3000}
                      onNotificationClose={() => {
                        setModalBoxStatus('close');
                      }}
                    />
                  ),
                  notificationModal: true,
                });
                setModalBoxStatus('open');
              }
            }}
            sidebarClosed={() => setAddAccountSidePanelOpen(false)}
          />
        }
      />

      <div className="add-station-tabs">
        <TabToggle
          tabs={[t('stationInformation'), t('operatingHours')]}
          defaultTab="stationInformation"
          onChange={changeTabPages}
        />
      </div>
      {openTab === 0 && (
        <div className="panel-form">
          <div className="add-station-from">
            {(PortalType.NETWORKS === portalType || PortalType.APP === portalType) && (
              <div className="account-select-wrapper">
                <SelectFiled
                  name="accountCustomId"
                  inputLabel={t('accountName')}
                  placeholder={t('selectAccountName')}
                  options={accountNameList}
                  loading={isAccountLoading}
                  showInformationIcon
                  onInformationClick={(options) => handleOpenFacilityInformation(options)}
                  description={(option) => option.value}
                  selectedValue={stationDetails.accountCustomId ?? ''}
                  onChangeValue={handleStationDetailChange}
                  error={formStatus.showFormErrors && !stationDetails.accountCustomId}
                  errorMessage={t('accountNameRequired')}
                  valueRequired
                  enableSearch
                  disabled={!!fetchByPartner}
                  tooltipText={t('accountNameTooltip')}
                />
                {!fetchByPartner && !isAccountLoading && accountNameList.length === 0 && (
                  <div className="add-partner-btn-wrap">
                    <MyButton
                      label={t('addAccount')}
                      buttonType="primary-outline"
                      onClickFunc={handleOpenAddPartner}
                      svgIcon={<PlusIcon />}
                    />
                  </div>
                )}
              </div>
            )}
            <CustomInput
              inputLabel={t('stationName')}
              type="text"
              placeholder={t('enterName')}
              name="name"
              value={stationDetails.name}
              onChangeValue={handleStationDetailChange}
              valueRequired
              error={formStatus.showFormErrors && !stringValidation(1, 100, stationDetails.name)}
              errorMessage={`${t('stationName')} ${t('required')}`}
              requiredTooltip
              tooltipText={t('stationNameTooltip')}
            />
          </div>
          <div className="form-bottom">
            <CustomAddressInput
              inputLabel={t('address')}
              placeholder={t('enterAddress')}
              name="address"
              value={stationDetails.address}
              onChangeValue={handleChangeAddress}
              valueRequired
              error={
                (formStatus.showFormErrors &&
                  (!addressValidation(500, stationDetails.address) ||
                    !isAddressResolved(stationDetails))) ||
                !!latLongError
              }
              errorMessage={
                latLongError ||
                (addressValidation(500, stationDetails.address)
                  ? t('addressSelectFromSuggestionsError')
                  : t('addressRequiredError'))
              }
              tooltipText={t('addressTooltip')}
            />
          </div>
          <div className="add-station-from form-bottom">
            {stationDetails?.geoLocations?.map((location) => (
              <CustomInput
                key={location.key}
                inputLabel={location.name}
                type="text"
                name={location.key}
                value={location.value}
                onChangeValue={() => {}}
                readOnly
                placeholder=""
              />
            ))}
          </div>
          <div className="form-bottom">
            <SelectLocation
              inputLabel={t('latitudeLongitude')}
              latitude={stationDetails.latitude}
              longitude={stationDetails.longitude}
              onChangeValue={handleChangeLatLong}
              valueRequired
              error={
                (formStatus.showFormErrors &&
                  !(stationDetails.latitude && stationDetails.longitude)) ||
                !!latLongError
              }
              errorMessage={latLongError || t('latLongRequiredError')}
              tooltipText={t('latLongTooltip')}
            />
          </div>
          {/* Emergency Contact & Electricity - Optional */}
          <div className="add-station-from form-bottom">
            <CustomPhoneInput
              inputLabel={t('emergencyContact')}
              type="number"
              placeholder={t('enterEmergencyContact')}
              name="emergencyContact"
              value={stationDetails.emergencyContact || ''}
              value1={stationDetails.countryCode}
              onChangeValue={handleStationDetailChange}
              valueRequired={false}
              tooltipText={t('emergencyContactTooltip')}
              minLength={5}
              maxLength={13}
            />
            <CustomInput
              inputLabel={t('usageCost')}
              type="number"
              placeholder={t('enterUsageCost')}
              name="electricityCostByUtilityProvider"
              value={stationDetails.electricityCostByUtilityProvider}
              onChangeValue={handleStationDetailChange}
              valueRequired={false}
              error={
                stationDetails.electricityCostByUtilityProvider !== null &&
                stationDetails.electricityCostByUtilityProvider !== undefined &&
                Number(stationDetails.electricityCostByUtilityProvider) < 0.1
              }
              errorMessage={t('electricityCostMinError')}
              requiredTooltip
              tooltipText={t('electricityCostTooltip')}
              decimalLimit={3}
              maxValue={1000}
            />
            <div className="electricity-contract-type-wrap">
              <SelectFiled
                inputLabel={t('electricityContract')}
                name="electricityContractType"
                placeholder={t('chooseContractType')}
                selectedValue={stationDetails.electricityContractType}
                onChangeValue={handleStationDetailChange}
                options={listOfCountryCode}
                error={false}
                errorMessage={t('electricityContError')}
                tooltipText={t('chooseContractTypeTooltip')}
              />
            </div>
          </div>
          {/* Upload Media - Optional */}
          <div className="form-bottom">
            <CustomMediaUpload
              inputLabel={t('uploadMedia')}
              acceptedFileType=".jpg,.jpeg,.png,"
              placeholder={t('uploadMediaPlaceholder')}
              name="medias"
              selectedFiles={stationDetails.medias}
              existingFiles={
                stationDetails.medias.length === 0
                  ? [
                      {
                        fileName: 'default-station.png',
                        fileSize: 0,
                        fileType: 'image/png',
                        url: DefaultStationImage,
                        signedUrl: DefaultStationImage,
                      },
                    ]
                  : undefined
              }
              valueRequired={false}
              onChangeValue={handleStationDetailChange}
              error={false}
              errorMessage={t('documentRequiredError')}
              tooltipText={t('mediaTooltip')}
              maxImageSizeMB={1}
            />
          </div>
          {/* Amenities */}
          <div className="form-bottom">
            {' '}
            <AmenitiesInput
              selectedAmenities={stationDetails.amenities}
              handleStationDetailChange={handleStationDetailChange}
              tooltipText={t('amenitiesTooltip')}
              dynamicAmenities={dynamicAmenities.length > 0 ? dynamicAmenities : undefined}
              isLoading={isFetchingAmenities}
            />
          </div>
          {/* Navigation Directions */}
          <div className="form-bottom navigation-directions-section">
            <div className="navigation-directions-header">
              <p className="title">{t('navigationDirections')}</p>
              {navigationDirections.length < 4 && (
                <button
                  type="button"
                  className="add-direction-btn"
                  onClick={handleAddNavigationDirection}>
                  + {t('add')}
                </button>
              )}
            </div>

            <div className="navigation-directions-list">
              {navigationDirections.map((item, index) => {
                const availableOptions = getAvailableLanguageOptions(index);
                return (
                  <div key={index} className="navigation-direction-row">
                    <div className="field-select">
                      <SelectFiled
                        inputLabel={t('language')}
                        name={`language-${index}`}
                        placeholder={t('selectLanguage')}
                        selectedValue={item.language}
                        onChangeValue={(_, value) => {
                          const updated = [...navigationDirections];
                          updated[index].language = value as string;
                          // Clear directions if language changes
                          updated[index].direction = '';
                          setNavigationDirections(updated);
                        }}
                        options={availableOptions}
                        triggerOptions={getLanguageOptions()}
                      />
                    </div>
                    <div className="field-input">
                      <CustomInput
                        inputLabel={t('directions')}
                        type="text"
                        placeholder={
                          item.language
                            ? `${t('enterDirectionsIn')} ${item.language}`
                            : t('selectLanguageFirst')
                        }
                        name={`directions-${index}`}
                        value={item.direction}
                        onChangeValue={(name, value) =>
                          handleDirectionsChange(index, value as string)
                        }
                        disabled={!item.language}
                        hint={false}
                      />
                    </div>
                    {navigationDirections.length > 1 && (
                      <button
                        type="button"
                        className="remove-direction-btn"
                        onClick={() => {
                          setNavigationDirections(
                            navigationDirections.filter((_, index_) => index_ !== index),
                          );
                        }}>
                        {t('remove')}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          {/* Documents Section */}
          <div className="form-bottom documents-section">
            <div className="documents-header">
              <p className="title">{t('documents')}</p>
              {additionalDocs.length < 2 && (
                <button
                  type="button"
                  className="add-document-btn"
                  onClick={() => setAdditionalDocs([...additionalDocs, []])}>
                  + {t('add')}
                </button>
              )}
            </div>
            <div className="documents-grid">
              <CustomFileInput
                inputLabel={t('ebReportLabel')}
                acceptedFileType="application/pdf"
                placeholder={t('uploadPdf')}
                name="ebReport"
                selectedFiles={stationDetails.ebReport}
                onChangeValue={handleStationDetailChange}
                valueRequired={false}
                maxFileSizeMB={1}
                error={
                  formStatus.showFormErrors &&
                  stationDetails.ebReport.length > 0 &&
                  !checkFileSizeValidation(stationDetails.ebReport, 1, 1)
                }
                errorMessage={t('documentRequiredError')}
                tooltipText={t('ebReportTooltip')}
              />
              <CustomFileInput
                inputLabel={t('landDocument')}
                acceptedFileType="image/*, application/pdf"
                placeholder={t('uploadPdf')}
                name="landOwnerShipDocument"
                selectedFiles={stationDetails.landOwnerShipDocument}
                onChangeValue={handleStationDetailChange}
                valueRequired={false}
                maxFileSizeMB={1}
                error={
                  formStatus.showFormErrors &&
                  stationDetails.landOwnerShipDocument.length > 0 &&
                  !checkFileSizeValidation(stationDetails.landOwnerShipDocument, 1, 1)
                }
                errorMessage={t('documentRequiredError')}
                tooltipText={t('landDocumentTooltip')}
              />
              {additionalDocs.map((files, index) => (
                <div key={index} className="additional-document-field-wrap">
                  <CustomFileInput
                    inputLabel={`${t('additionalDocument')} ${index + 1}`}
                    acceptedFileType="image/*, application/pdf"
                    placeholder={t('uploadPdf')}
                    name={`additionalDocument-${index}`}
                    selectedFiles={files}
                    onChangeValue={(name, selectedFileList) => {
                      const updated = [...additionalDocs];
                      updated[index] = selectedFileList;
                      setAdditionalDocs(updated);
                    }}
                    valueRequired={false}
                    error={false}
                    errorMessage={t('documentRequiredError')}
                    maxFileSizeMB={1}
                    tooltipText={t('additionalDocumentTooltip')}
                  />
                  <button
                    type="button"
                    className="remove-document-btn"
                    onClick={() => {
                      // eslint-disable-next-line unicorn/prevent-abbreviations
                      setAdditionalDocs(additionalDocs.filter((_, idx) => idx !== index));
                    }}>
                    {t('remove')}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {openTab === 1 && (
        <div className="operating-hours-container">
          <div className="timezone-section">
            <SelectFiled
              inputLabel={t('timeZone')}
              name="timeZone"
              placeholder={t('selectTimeZone')}
              selectedValue={stationDetails.timeZone || ''}
              onChangeValue={(name, value) =>
                setLocationDetails((previous) => ({
                  ...previous,
                  [name]: normalizeTimeZone(value as string),
                }))
              }
              options={timeZoneOptions}
              enableSearch
              valueRequired
              tooltipText={t('timeZoneTooltip')}
            />
          </div>

          <div className="opening-hours-section">
            <div className="section-header">
              <p className="section-title"> </p>
              <div className="switch-buttons-group">
                <div className="switch-button">
                  <p>{t('allDay')}</p>
                  <div>
                    <CustomInputSwitch
                      inputLabel=""
                      name="allDay"
                      value={stationDetails.operatingHours?.allDay}
                      onChangeValue={handleAllDayToggle}
                    />{' '}
                  </div>
                </div>
              </div>
            </div>

            <div className="days-config-layout">
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(
                (day) => {
                  const dayData = stationDetails.operatingHours?.[
                    day as keyof OperatingHours
                  ] as any;
                  return (
                    <div className="day-config-row" key={day}>
                      <div className="day-header-inline">
                        <div className="day-toggle-name">
                          <CustomInputSwitch
                            name={day}
                            value={dayData?.isOpen}
                            onChangeValue={(name, value) => handleDayToggle(name as DayKey, value)}
                          />
                          <p className="day-name">{t(day).slice(0, 3)}</p>
                        </div>

                        {dayData?.isOpen ? (
                          <div className="day-controls-inline">
                            <div className="twenty-four-hour-toggle">
                              <CustomInputSwitch
                                name={`${day}-24h`}
                                inputLabel=""
                                value={dayData?.is24Hours || false}
                                onChangeValue={(_n, value) => {
                                  if (value) {
                                    handleOperatingHoursChange(day as DayKey, 'is24Hours', true);
                                    handleOperatingHoursChange(day as DayKey, 'from', '00:00');
                                    handleOperatingHoursChange(day as DayKey, 'to', '23:59');
                                  } else {
                                    handleOperatingHoursChange(day as DayKey, 'is24Hours', false);
                                    handleOperatingHoursChange(day as DayKey, 'from', '09:00');
                                    handleOperatingHoursChange(day as DayKey, 'to', '18:00');
                                  }
                                }}
                              />
                              <p className="twenty-four-label">{t('twentyFourHours')}</p>
                            </div>
                            <div className="time-field-wrap">
                              <CustomTimePicker
                                innerLabel={t('from')}
                                name={`${day}-from`}
                                value={dayData?.from}
                                disabled={dayData?.is24Hours}
                                onChangeValue={(_n, v) =>
                                  handleOperatingHoursChange(day as DayKey, 'from', v)
                                }
                              />
                            </div>
                            <div className="time-field-wrap">
                              <CustomTimePicker
                                innerLabel={t('to')}
                                name={`${day}-to`}
                                value={dayData?.to}
                                disabled={dayData?.is24Hours}
                                onChangeValue={(_n, v) =>
                                  handleOperatingHoursChange(day as DayKey, 'to', v)
                                }
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="day-summary">
                            <span className="status-text closed">{t('closed')}</span>
                            <span className="time-range">—</span>
                          </div>
                        )}
                      </div>

                      {dayData?.isOpen &&
                        dayData.from &&
                        dayData.to &&
                        dayData.from >= dayData.to &&
                        formStatus.showFormErrors &&
                        !stationDetails.operatingHours?.allDay && (
                          <p
                            className="text-danger m-0 pt-1"
                            style={{ fontSize: '12px', paddingLeft: '140px' }}>
                            {t('closeTimeGreaterThanOpenTime')}
                          </p>
                        )}
                    </div>
                  );
                },
              )}
            </div>
          </div>
        </div>
      )}
      <div className="add-station-button-wrap">
        <div className="panel-footer">
          <MyButton label={t('cancel')} buttonType="back" onClickFunc={handleClickCancel} />
          <MyButton
            label={t('submit')}
            buttonType="submit"
            onClickFunc={handleSubmitLocationDetails}
            loading={formStatus.buttonLoader}
            disableButton={formStatus.buttonLoader}
          />
        </div>
      </div>
    </div>
  );
}

export default AddLocation;

AddLocation.defaultProps = {
  /**
   * Callback function to be called when the close button is clicked
   */
  onClose: () => {},
  /**
   * Callback function to be called when the station is added successfully
   */
  onSuccess: () => {},
};
